import React, { useContext, useState, useEffect, useMemo } from "react";
import clsx from "clsx";
import { useHistory } from "react-router-dom";
import { Box, Typography } from "@material-ui/core";
import dayjs from "dayjs";
import {
  B2BSpinner,
  LoadingIndicator,
  B2BTextField,
  TravelerSelectWorkflow as HalifaxTravelerSelectWorkflow,
  TravelerSelectStep as HalifaxTravelerSelectStep,
} from "halifax";
import {
  IPerson,
  PersonId,
  ExperiencesAgeBandEnum,
  CLICKED_ADD_TRAVELER,
  ClickedAddTravelerProperties,
} from "redmond";
import {
  getParentState,
  ParentState,
  useCheckoutStateSelector as useSelector,
  useCheckoutState,
} from "@capone/checkout";

import { useIsTravelApp, isVesperTenant, getConfigTenant } from "@capone/common";
import {
  TravelerSelectWorkflow as LocalTravelerSelectWorkflow,
  TravelerSelectStep as LocalTravelerSelectStep,
  // TravelerGroup is local-only — halifax has no equivalent export, so it
  // always comes from the local source regardless of the experiment flag.
  TravelerGroup,
} from "./LocalTravelerSelectWorkflow";
import * as constants from "./textConstants";
import { ClientContext } from "../../../../App";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import {
  addTrackingProperties,
  isCheckoutImprovementsEnabled,
  useExperiments,
} from "../../../../context/experiments";
import { config } from "../../../../api/config";
import { Event, TEvent } from "../../state/events";
import { ActivityMachineContext } from "../../state/types";
import { PriceBreakdown } from "..";
import "./styles.scss";
import { DesktopActivityBookValidationError } from "../DesktopActivityBookWorkflow";
import { personToIPerson } from "../../../../utils/personToIPerson";
import { ActivityTravelerSelectors } from "../../../../checkout";
import { ActivitySelectors } from "../../state/selectors";
import { getTotalTravelersCount } from "../../../common";

const formatAgeBandLabel = (ageBand: ExperiencesAgeBandEnum): string => {
  switch (ageBand) {
    case ExperiencesAgeBandEnum.ADULT:
      return "Adults";
    case ExperiencesAgeBandEnum.CHILD:
      return "Children";
    case ExperiencesAgeBandEnum.SENIOR:
      return "Seniors";
    case ExperiencesAgeBandEnum.YOUTH:
      return "Youth";
    case ExperiencesAgeBandEnum.INFANT:
      return "Infants";
    case ExperiencesAgeBandEnum.TRAVELER:
    default:
      return "Travelers";
  }
};

const AGE_BAND_ORDER: ExperiencesAgeBandEnum[] = [
  ExperiencesAgeBandEnum.ADULT,
  ExperiencesAgeBandEnum.CHILD,
  ExperiencesAgeBandEnum.INFANT,
  ExperiencesAgeBandEnum.SENIOR,
  ExperiencesAgeBandEnum.TRAVELER,
  ExperiencesAgeBandEnum.YOUTH,
];

const getAgeBandSortIndex = (ageBand: ExperiencesAgeBandEnum): number => {
  const index = AGE_BAND_ORDER.indexOf(ageBand);
  return index === -1 ? AGE_BAND_ORDER.length : index;
};

const getTravelerWorkflowStep = ({
  openTravelerFormModal,
  isMobile,
  parentState,
  isCheckoutImprovementsAvailable,
}: {
  openTravelerFormModal: boolean;
  isMobile: boolean;
  parentState: ParentState;
  isCheckoutImprovementsAvailable: boolean;
}): LocalTravelerSelectStep | HalifaxTravelerSelectStep => {
  // Enum values are numerically identical between halifax and local
  // (Main = 0, TravelerInfoForm = 1, TravelerSelect = 2, TravelerDeleteModal = 3),
  // so the returned values work for either workflow at runtime.
  switch (true) {
    case openTravelerFormModal:
      return isCheckoutImprovementsAvailable
        ? LocalTravelerSelectStep.TravelerInfoForm
        : HalifaxTravelerSelectStep.TravelerInfoForm
    case isMobile && parentState !== ParentState.review:
      return isCheckoutImprovementsAvailable
        ? LocalTravelerSelectStep.TravelerSelect
        : HalifaxTravelerSelectStep.TravelerSelect;
    default:
      return isCheckoutImprovementsAvailable
        ? LocalTravelerSelectStep.Main
        : HalifaxTravelerSelectStep.Main;
  }
};

export interface ITravelerSelectionProps {
  isMobile?: boolean;
  validationErrorTypes?: DesktopActivityBookValidationError[];
  showNonModalContent?: boolean;
  isXmodRedesign?: boolean;
}

export const TravelerSelection = ({
  isMobile,
  validationErrorTypes,
  showNonModalContent,
  isXmodRedesign,
}: ITravelerSelectionProps) => {
  const history = useHistory();
  const [state, send] = useCheckoutState<TEvent, ActivityMachineContext>();

  const { isTravelApp } = useIsTravelApp();
  const isVesper = isVesperTenant(getConfigTenant());

  const parentState = getParentState(state.value) as ParentState;

  const openTravelerFormModal = useSelector(
    ActivityTravelerSelectors.getOpenTravelerFormModal
  );

  const travelers = useSelector(ActivityTravelerSelectors.getUserTravelers);
  const selectedTravelerIds = useSelector(
    ActivityTravelerSelectors.getSelectedTravelerIds
  );

  const travelersLoading = useSelector(
    ActivityTravelerSelectors.getIsTravelerInformationLoading
  );

  const hasError = useSelector(ActivityTravelerSelectors.getTravelerErrorOpen);

  const currentTraveler = useSelector(
    ActivityTravelerSelectors.getCurrentUserTraveler
  );

  const areSelectedTravelersValidForAgeBands = useSelector(
    ActivityTravelerSelectors.getAreSelectedTravelersValidForAgeBands
  );

  const getTravelerTypeByAge = useSelector(
    ActivityTravelerSelectors.getTravelerTypeByAge
  );

  const distinctAgeBands = useSelector(
    ActivityTravelerSelectors.getDistinctAgeBands
  );

  const priceBreakdown = useSelector(ActivitySelectors.getActivityPriceBreakdown);

  const {
    requirePassport,
    requireWeight,
    requireHeight,
    hasAnsweredBookingQuestions,
    getCurrentTravelerBookingQuestionResponse,
  } = useSelector(ActivityTravelerSelectors.getPerTravelerBookingQuestion);

  const searchedTravelerCount = getTotalTravelersCount(
    useSelector(ActivitySelectors.getActivityTravelerCounts)
  );

  const [weight, setWeight] = useState("");
  const [heightFt, setHeightFt] = useState("");
  const [heightIn, setHeightIn] = useState("");
  const [hasMissingFields, setHasMissingFields] = useState(false);
  const [weightError, setWeightError] = useState<string | undefined>();
  const [heightFtError, setHeightFtError] = useState<string | undefined>();
  const [heightInError, setHeightInError] = useState<string | undefined>();

  const expState = useExperiments();
  const isCheckoutImprovementsAvailable = isCheckoutImprovementsEnabled(
    expState.experiments
  );
  // Pick the enum source based on the flag for symmetry with the workflow swap.
  // Note: values are numerically identical in both sources, so this is a
  // declarative/intent-only swap — runtime behavior is unchanged either way.

  // Groups travelers by age band for the shared TravelerSelectWorkflow's `travelerGroups` prop.
  // Returns `undefined` when the activity has no distinct age bands (single TRAVELER fallback) so
  // the workflow renders its flat list.
  const ageBandGroups: TravelerGroup[] | undefined = useMemo(() => {
    const groups: TravelerGroup[] = distinctAgeBands
      .map((band) => {
        const lineItem = priceBreakdown?.lineItems.find(
          (li) => li.specificAgeBand.ageBand === band.ageBand
        );
        const required = lineItem?.specificAgeBand.numberOfTravelers ?? 0;
        const travelerIds = travelers
          .filter((t) => {
            if (!t.dateOfBirth) return false;
            const age = dayjs().diff(dayjs(t.dateOfBirth), "year");
            return age >= band.lowerBound && age < band.upperBound;
          })
          .map((t) => t.id);

        return {
          id: band.ageBand,
          label: formatAgeBandLabel(band.ageBand as ExperiencesAgeBandEnum),
          sublabel: `(Ages ${band.lowerBound} – ${band.upperBound})`,
          travelerIds,
          required,
        };
      })
      .filter((group) => group.required > 0)
      .sort(
        (a, b) =>
          getAgeBandSortIndex(a.id as ExperiencesAgeBandEnum) -
          getAgeBandSortIndex(b.id as ExperiencesAgeBandEnum)
      )
      .map((group, index, arr) => ({
        ...group,
        isLast: arr.length - 1 === index,
      }));

    return groups.length > 0 ? groups : undefined;
  }, [distinctAgeBands, priceBreakdown, travelers]);

  const validateWeight = (value: string): string | undefined => {
    if (!value || value.trim() === "") {
      return constants.REQUIRED_FIELD;
    }
    const numValue = parseFloat(value);
    if (Number.isNaN(numValue) || numValue < 1 || numValue > 1000) {
      return constants.WEIGHT_INVALID;
    }
    return undefined;
  };

  const validateHeightFt = (value: string): string | undefined => {
    if (!value || value.trim() === "") {
      return constants.REQUIRED_FIELD;
    }
    const numValue = parseInt(value, 10);
    if (Number.isNaN(numValue) || numValue < 0 || numValue > 8) {
      return constants.HEIGHT_FT_INVALID;
    }
    return undefined;
  };

  const validateHeightIn = (value: string): string | undefined => {
    if (!value || value.trim() === "") {
      return constants.REQUIRED_FIELD;
    }
    const numValue = parseInt(value, 10);
    if (Number.isNaN(numValue) || numValue < 0 || numValue > 11) {
      return constants.HEIGHT_IN_INVALID;
    }
    return undefined;
  };

  const validatePassport = (passport?: IPerson["passport"]): string | undefined => {
    if (!passport) {
      return constants.REQUIRED_FIELD;
    }

    // Check country of issue
    if (!passport.countryOfIssue || passport.countryOfIssue.trim() === "") {
      return constants.PASSPORT_COUNTRY_REQUIRED;
    }

    // Check passport number - must be non-empty and 6-9 characters
    if (!passport.number || passport.number.trim() === "") {
      return constants.REQUIRED_FIELD;
    }

    const trimmedNumber = passport.number.trim();
    if (trimmedNumber.length < 6 || trimmedNumber.length > 9) {
      return constants.PASSPORT_NUMBER_INVALID;
    }

    // Check expiration
    if (!passport.expiration || passport.expiration.trim() === "") {
      return constants.PASSPORT_EXPIRATION_REQUIRED;
    }

    // Validate expiration date is in the future
    const expirationDate = dayjs(passport.expiration, "YYYY-MM-DD", true);
    if (expirationDate.isValid() && expirationDate.isBefore(dayjs(), "day")) {
      return constants.PASSPORT_EXPIRATION_INVALID;
    }

    return undefined;
  };

  useEffect(() => {
    if (currentTraveler) {
      const currentTravelerResponse = getCurrentTravelerBookingQuestionResponse(
        currentTraveler.id
      );

      setWeight(currentTravelerResponse.weight);
      setHeightFt(currentTravelerResponse.heightFt);
      setHeightIn(currentTravelerResponse.heightIn);

      // Clear validation errors when loading existing data
      setWeightError(undefined);
      setHeightFtError(undefined);
      setHeightInError(undefined);
      setHasMissingFields(false);
    } else {
      // Reset fields when opening form for a new traveler
      setWeight("");
      setHeightFt("");
      setHeightIn("");
      setWeightError(undefined);
      setHeightFtError(undefined);
      setHeightInError(undefined);
      setHasMissingFields(false);
    }
  }, [currentTraveler]);

  const handleSelectTraveler = (
    travelerId: PersonId,
    singleTravelerWorkflow?: boolean
  ) => {
    if (hasError) {
      send(Event.CLEAR_TRAVELER_INFORMATION_ERROR);
    }

    const selectedTraveler = travelers.find((p) => p.id === travelerId);
    if (selectedTraveler) {
      send({
        type: Event.SET_CURRENT_TRAVELER,
        traveler: selectedTraveler as IPerson,
      });
    }

    if (!hasAnsweredBookingQuestions(travelerId)) {
      send(Event.OPEN_TRAVELER_FORM);
      return;
    }

    send({
      type: Event.SELECT_TRAVELER,
      travelerId,
      singleTravelerWorkflow,
    });
  };

  const handleUpdateTraveler = (traveler: IPerson) => {
    // Validate required fields and their values
    let hasValidationErrors = false;

    if (requirePassport) {
      const passportValidationError = validatePassport(traveler.passport);
      if (passportValidationError) {
        hasValidationErrors = true;
        // PassportInfoForm displays its own error messages
      }
    }

    if (requireWeight) {
      const weightValidationError = validateWeight(weight);
      setWeightError(weightValidationError);
      if (weightValidationError) {
        hasValidationErrors = true;
      }
    }

    if (requireHeight) {
      const heightFtValidationError = validateHeightFt(heightFt);
      const heightInValidationError = validateHeightIn(heightIn);
      setHeightFtError(heightFtValidationError);
      setHeightInError(heightInValidationError);
      if (heightFtValidationError || heightInValidationError) {
        hasValidationErrors = true;
      }
    }

    if (hasValidationErrors) {
      setHasMissingFields(true);
      return;
    }

    if (requirePassport || requireWeight || requireHeight) {
      send({
        type: Event.ANSWER_BOOKING_QUESTIONS,
        travelerId: traveler.id,
        data: {
          heightFt,
          heightIn,
          weight,
        },
      });
    }

    send({
      type: Event.UPDATE_TRAVELER,
      person: {
        ...traveler,
        // Only include passport if all fields are valid (already validated above)
        passport:
          traveler.passport?.countryOfIssue &&
            traveler.passport?.countryOfIssue.trim() &&
            traveler.passport?.expiration &&
            traveler.passport?.expiration.trim() &&
            traveler.passport?.number &&
            traveler.passport?.number.trim()
            ? traveler.passport
            : undefined,
      },
      onUpdate: true,
    });
  };

  const handleDeleteTraveler = (personId: string) => {
    send({ type: Event.DELETE_TRAVELER, personId });
  };

  const handleEditClick = (traveler: IPerson) => {
    if (parentState === ParentState.review) {
      // edit travelers from mobile review screen
      send({
        type: Event.GO_TO_TRAVELER_SELECT,
      });
    }

    send({
      type: Event.SET_CURRENT_TRAVELER,
      traveler,
    });
    send(Event.OPEN_TRAVELER_FORM);
  };

  const handleFormClose = () => {
    send(Event.OPEN_TRAVELER_PICKER);
  };

  const handleClickAddNewTraveler = () => {
    // Reset height/weight fields for new traveler
    setWeight("");
    setHeightFt("");
    setHeightIn("");
    setWeightError(undefined);
    setHeightFtError(undefined);
    setHeightInError(undefined);
    setHasMissingFields(false);

    send({
      type: Event.SET_CURRENT_TRAVELER,
      traveler: undefined,
    });
    send(Event.OPEN_TRAVELER_FORM);
  };

  const handleContinue = () => {
    if (!areSelectedTravelersValidForAgeBands) {
      // show error message
      const errorElement = document.querySelector(".age-band-error-message");
      if (errorElement) {
        errorElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
      return;
    }
    send(Event.NEXT);
  };

  const handleGoBack = () => {
    history.goBack();
  };

  const { sessionInfo } = useContext(ClientContext);

  if (travelersLoading) {
    return (
      <LoadingIndicator
        className="flight-book-passenger-selection-loading-indicator"
        indicatorSize="small"
        indicator={B2BSpinner}
        message={constants.UPDATE_TEXT}
      />
    );
  }

  return isCheckoutImprovementsAvailable ? (
    <LocalTravelerSelectWorkflow
      showErrors={{ travelerSelect: validationErrorTypes?.includes("travelers") }}
      showGenderField
      showNationalityField
      requireNationality
      showDefaultAddNewTravelerAction={false}
      // show passport section if pre traveler booking question requires it
      showPassportSection={requirePassport}
      requirePassport={requirePassport}
      className={clsx("activity-traveler-selection-root", {
        error: validationErrorTypes?.includes("travelers"),
        xmod: isXmodRedesign,
        redesigned: isVesper,
      })}
      customHeader={
        <Box className="activity-flight-book-passenger-mobile-header">
          <Typography variant="h3" className="pax-header-primary-title">
            {constants.TRAVELER_INFO_TEXT}
          </Typography>
        </Box>
      }
      travelers={travelers.map(personToIPerson)}
      travelerGroups={ageBandGroups}
      progress={getTravelerWorkflowStep({
        openTravelerFormModal,
        isMobile: !!isMobile,
        parentState,
        isCheckoutImprovementsAvailable,
      })}
      setProgress={() => {}} // progress is derived from state machine
      userInfo={sessionInfo?.userInfo}
      titles={{
        travelerInfoTitle:
          isMobile &&
          (parentState === ParentState.review ||
            parentState === ParentState.activityTravelerInformation)
            ? constants.TRAVELER_INFO_TEXT
            : constants.TRAVELER_INFO_TITLE_UPDATED(searchedTravelerCount),
        travelerInfoSubtitle: undefined,
        frequentFlyerTitle: "",
        additionalInfoTitle: "",
        adultTitle: constants.ADULT_TITLE,
        childTitle: constants.CHILD_TITLE,
        infantSeatTitle: "",
        infantLapTitle: "",
        addTravelers: constants.ADD_TRAVELERS_TEXT_UPDATED,
        editTravelerTitle: constants.EDIT_TRAVELER_TEXT,
        travelerInfoFormSubtitle: constants.ADD_TRAVELERS_SUBTITLE,
        travelerInfoSectionTitle: constants.TRAVELER_INFO_TEXT,
        passportTitle: constants.PASSPORT_TITLE,
        passportSubtitle: constants.PASSPORT_SUBTITLE,
        completeTravelerProfileTitle: constants.COMPLETE_PROFILE_TITLE,
        completeTravelerProfileSubtitle: constants.COMPLETE_PROFILE_SUBTITLE,
        noTravelersTitle: constants.NO_TRAVELERS_TITLE,
      }}
      selectedTravelerIds={selectedTravelerIds}
      handleSelectTraveler={handleSelectTraveler}
      handleUpdatePassenger={handleUpdateTraveler}
      handleDeletePassenger={handleDeleteTraveler}
      isMobile={isMobile}
      buttonClassName="b2b"
      isFlights
      onClickAddNewTraveler={() => {
        handleClickAddNewTraveler();
        trackEvent({
          eventName: CLICKED_ADD_TRAVELER,
          properties: addTrackingProperties(expState.trackingProperties, {
            entry_type: "checkout",
          } as ClickedAddTravelerProperties),
        });
      }}
      onClickEditTraveler={(traveler) => handleEditClick(traveler)}
      tenant={config.TENANT}
      trackEvent={trackEvent}
      setSelectedTravelerIds={() => {}}
      editPassenger={
        currentTraveler ? personToIPerson(currentTraveler) : undefined
      }
      onTravelerFormClose={handleFormClose}
      onContinue={isMobile ? handleContinue : undefined}
      onGoBack={isMobile ? handleGoBack : undefined}
      selectionScreenHeaderElement={<PriceBreakdown isMobile dropdown />}
      mobileTravelerRowType="checkbox"
      updatedDesign
      showMobilePopoverTransition={false}
      showMobileChangeCTA={false}
      bypassSelectForSingleTraveler={false}
      showNonModalContent={showNonModalContent}
      addNewTravelerButton
      showAdditionalInfoSection={requireWeight || requireHeight}
      renderCustomAdditionalInfoSection={(errorOnSave: boolean) => {
        const hasFieldErrors = errorOnSave || hasMissingFields;

        return (
          <Box className="traveler-info-form-section activity-additional-info-container">
            <Box className="traveler-info-description">
              <Typography className="section-title">
                {constants.ADDITIONAL_INFO_TITLE}
              </Typography>
              <Typography className="section-subtitle">
                {constants.ADDITIONAL_INFO_SUBTITLE}
              </Typography>
            </Box>
            <form
              className={clsx("additional-fields-form", {
                mobile: isMobile,
              })}
            >
              {requireWeight && (
                <B2BTextField
                  className={clsx("activity-weight-input-field")}
                  value={weight}
                  onChange={(value) => {
                    const filtered = value
                      .replace(/[^0-9.]/g, "")
                      .replace(/(\..*)\./g, "$1");
                    setWeight(filtered);
                  }}
                  onFocus={() => {
                    setWeightError(undefined);
                    setHasMissingFields(false);
                  }}
                  onBlur={() => {
                    if (weight) {
                      const error = validateWeight(weight);
                      setWeightError(error);
                    }
                  }}
                  label={constants.WEIGHT_LBS_LABEL}
                  error={!!weightError && hasFieldErrors}
                  errorHelper={weightError}
                  fullWidth
                  required
                />
              )}
              {requireHeight && (
                <Box className="activity-height-input-container">
                  <B2BTextField
                    className="activity-height-input-ft-field"
                    value={heightFt}
                    onChange={(value) => {
                      setHeightFt(value.replace(/[^0-9]/g, ""));
                    }}
                    onFocus={() => {
                      setHeightFtError(undefined);
                      setHasMissingFields(false);
                    }}
                    onBlur={() => {
                      if (heightFt) {
                        const error = validateHeightFt(heightFt);
                        setHeightFtError(error);
                      }
                    }}
                    label={constants.HEIGHT_FT_LABEL}
                    error={!!heightFtError}
                    errorHelper={heightFtError}
                    required
                  />
                  <B2BTextField
                    className="activity-height-input-inch-field"
                    value={heightIn}
                    onChange={(value) => {
                      setHeightIn(value.replace(/[^0-9]/g, ""));
                    }}
                    onFocus={() => {
                      setHeightInError(undefined);
                      setHasMissingFields(false);
                    }}
                    onBlur={() => {
                      if (heightIn) {
                        const error = validateHeightIn(heightIn);
                        setHeightInError(error);
                      }
                    }}
                    label={constants.HEIGHT_IN_LABEL}
                    error={!!heightInError}
                    errorHelper={heightInError}
                    required
                  />
                </Box>
              )}
            </form>
          </Box>
        );
      }}
      hideMobileContinueButton={
        selectedTravelerIds.length !== searchedTravelerCount
      }
      getTravelerTypeByAge={getTravelerTypeByAge}
      hideBackButton={isTravelApp}
    />
  ) : (
    <HalifaxTravelerSelectWorkflow
      showErrors={{ travelerSelect: validationErrorTypes?.includes("travelers") }}
      showGenderField
      showNationalityField
      requireNationality
      // show passport section if pre traveler booking question requires it
      showPassportSection={requirePassport}
      requirePassport={requirePassport}
      className={clsx("activity-traveler-selection-root", {
        error: validationErrorTypes?.includes("travelers"),
        xmod: isXmodRedesign,
        redesigned: isVesper,
      })}
      customHeader={
        <Box className="activity-flight-book-passenger-mobile-header">
          <Typography variant="h3" className="pax-header-primary-title">
            {constants.TRAVELER_INFO_TEXT}
          </Typography>
        </Box>
      }
      travelers={travelers.map(personToIPerson)}
      progress={getTravelerWorkflowStep({
        openTravelerFormModal,
        isMobile: !!isMobile,
        parentState,
        isCheckoutImprovementsAvailable,
      })}
      setProgress={() => {}} // progress is derived from state machine
      userInfo={sessionInfo?.userInfo}
      titles={{
        travelerInfoTitle:
          isMobile &&
          (parentState === ParentState.review ||
            parentState === ParentState.activityTravelerInformation)
            ? constants.TRAVELER_INFO_TEXT
            : constants.TRAVELER_INFO_TITLE_UPDATED(searchedTravelerCount),
        travelerInfoSubtitle: undefined,
        frequentFlyerTitle: "",
        additionalInfoTitle: "",
        adultTitle: constants.ADULT_TITLE,
        childTitle: constants.CHILD_TITLE,
        infantSeatTitle: "",
        infantLapTitle: "",
        addTravelers: constants.ADD_TRAVELERS_TEXT_UPDATED,
        editTravelerTitle: constants.EDIT_TRAVELER_TEXT,
        travelerInfoFormSubtitle: constants.ADD_TRAVELERS_SUBTITLE,
        travelerInfoSectionTitle: constants.TRAVELER_INFO_TEXT,
        passportTitle: constants.PASSPORT_TITLE,
        passportSubtitle: constants.PASSPORT_SUBTITLE,
        completeTravelerProfileTitle: constants.COMPLETE_PROFILE_TITLE,
        completeTravelerProfileSubtitle: constants.COMPLETE_PROFILE_SUBTITLE,
        noTravelersTitle: constants.NO_TRAVELERS_TITLE,
      }}
      selectedTravelerIds={selectedTravelerIds}
      handleSelectTraveler={handleSelectTraveler}
      handleUpdatePassenger={handleUpdateTraveler}
      handleDeletePassenger={handleDeleteTraveler}
      isMobile={isMobile}
      buttonClassName="b2b"
      isFlights
      onClickAddNewTraveler={() => {
        handleClickAddNewTraveler();
        trackEvent({
          eventName: CLICKED_ADD_TRAVELER,
          properties: addTrackingProperties(expState.trackingProperties, {
            entry_type: "checkout",
          } as ClickedAddTravelerProperties),
        });
      }}
      onClickEditTraveler={(traveler) => handleEditClick(traveler)}
      tenant={config.TENANT}
      trackEvent={trackEvent}
      setSelectedTravelerIds={() => {}}
      editPassenger={
        currentTraveler ? personToIPerson(currentTraveler) : undefined
      }
      onTravelerFormClose={handleFormClose}
      onContinue={isMobile ? handleContinue : undefined}
      onGoBack={isMobile ? handleGoBack : undefined}
      selectionScreenHeaderElement={<PriceBreakdown isMobile dropdown />}
      mobileTravelerRowType="checkbox"
      updatedDesign
      showMobilePopoverTransition={false}
      showMobileChangeCTA={false}
      bypassSelectForSingleTraveler={false}
      showNonModalContent={showNonModalContent}
      addNewTravelerButton
      showAdditionalInfoSection={requireWeight || requireHeight}
      renderCustomAdditionalInfoSection={(errorOnSave: boolean) => {
        const hasFieldErrors = errorOnSave || hasMissingFields;

        return (
          <Box className="traveler-info-form-section activity-additional-info-container">
            <Box className="traveler-info-description">
              <Typography className="section-title">
                {constants.ADDITIONAL_INFO_TITLE}
              </Typography>
              <Typography className="section-subtitle">
                {constants.ADDITIONAL_INFO_SUBTITLE}
              </Typography>
            </Box>
            <form
              className={clsx("additional-fields-form", {
                mobile: isMobile,
              })}
            >
              {requireWeight && (
                <B2BTextField
                  className={clsx("activity-weight-input-field")}
                  value={weight}
                  onChange={(value) => {
                    const filtered = value
                      .replace(/[^0-9.]/g, "")
                      .replace(/(\..*)\./g, "$1");
                    setWeight(filtered);
                  }}
                  onFocus={() => {
                    setWeightError(undefined);
                    setHasMissingFields(false);
                  }}
                  onBlur={() => {
                    if (weight) {
                      const error = validateWeight(weight);
                      setWeightError(error);
                    }
                  }}
                  label={constants.WEIGHT_LBS_LABEL}
                  error={!!weightError && hasFieldErrors}
                  errorHelper={weightError}
                  fullWidth
                  required
                />
              )}
              {requireHeight && (
                <Box className="activity-height-input-container">
                  <B2BTextField
                    className="activity-height-input-ft-field"
                    value={heightFt}
                    onChange={(value) => {
                      setHeightFt(value.replace(/[^0-9]/g, ""));
                    }}
                    onFocus={() => {
                      setHeightFtError(undefined);
                      setHasMissingFields(false);
                    }}
                    onBlur={() => {
                      if (heightFt) {
                        const error = validateHeightFt(heightFt);
                        setHeightFtError(error);
                      }
                    }}
                    label={constants.HEIGHT_FT_LABEL}
                    error={!!heightFtError}
                    errorHelper={heightFtError}
                    required
                  />
                  <B2BTextField
                    className="activity-height-input-inch-field"
                    value={heightIn}
                    onChange={(value) => {
                      setHeightIn(value.replace(/[^0-9]/g, ""));
                    }}
                    onFocus={() => {
                      setHeightInError(undefined);
                      setHasMissingFields(false);
                    }}
                    onBlur={() => {
                      if (heightIn) {
                        const error = validateHeightIn(heightIn);
                        setHeightInError(error);
                      }
                    }}
                    label={constants.HEIGHT_IN_LABEL}
                    error={!!heightInError}
                    errorHelper={heightInError}
                    required
                  />
                </Box>
              )}
            </form>
          </Box>
        );
      }}
      hideMobileContinueButton={
        selectedTravelerIds.length !== searchedTravelerCount
      }
      getTravelerTypeByAge={getTravelerTypeByAge}
      hideBackButton={isTravelApp}
    />
  );
};
